import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Clock,
  FileCheck,
  Menu,
  Scale,
  Shield,
  ShieldAlert,
  Timer,
  Users,
  X,
  Zap,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { POSTHOG_EVENTS } from "@/constants";

const faqItems = [
  {
    q: "Ce este NIS2 și cum afectează organizația mea?",
    a: "NIS2 (Directiva UE 2022/2555) stabilește cerințe minime de securitate cibernetică pentru organizațiile din sectoare esențiale și importante. În România, a fost transpusă prin OUG 155/2024 și Legea 124/2025. Dacă activezi în energie, transport, sănătate, infrastructură digitală sau alte sectoare critice, ești obligat să te conformezi.",
  },
  {
    q: "Care sunt termenele limită pentru conformare?",
    a: "Notificarea către DNSC trebuie făcută în 30 de zile de la intrarea în vigoare, autoevaluarea de risc în 60 de zile, iar monitorizarea și raportarea incidentelor sunt obligații continue. Nerespectarea termenelor poate duce la sancțiuni administrative.",
  },
  {
    q: "Ce riscuri am dacă nu mă conformez la NIS2?",
    a: "Amenzile pot ajunge până la 10.000.000 EUR sau 2% din cifra de afaceri anuală globală, cea mai mare valoare fiind aplicabilă. Pe lângă sancțiuni financiare, riscați interdicția temporară a activității și răspunderea personală a conducerii.",
  },
  {
    q: "Cât durează implementarea cu SECURENIS?",
    a: "Evaluarea inițială se completează în medie în 39 de minute. Un program complet de conformitate poate fi operațional în câteva săptămâni, comparativ cu 6-12 luni prin consultanță tradițională.",
  },
  {
    q: "Ce este CyFUN și cum se aplică în evaluarea maturității?",
    a: "CyFUN (Cybersecurity Framework for Unified National Approach) este cadrul de referință recomandat de DNSC pentru evaluarea maturității în securitate cibernetică. SECURENIS integrează nativ toate cele 217+ controale CyFUN.",
  },
  {
    q: "Cum demonstrez conformitatea către DNSC?",
    a: "Platforma generează automat dosarul de audit cu evidențe, justificări, versiuni și recomandări. Fiecare decizie are timestamp, justificare și istoric de modificări - gata pentru inspecția DNSC.",
  },
  {
    q: "Pot folosi platforma dacă sunt consultant extern?",
    a: "Da. Planul Consultant include management multi-entitate, dashboard consolidat pe toți clienții, export de rapoarte white-label și flux dedicat de colaborare cu echipele clientului.",
  },
  {
    q: "Ce garanții de securitate oferă platforma?",
    a: "Datele sunt stocate în UE, criptate în tranzit și la repaus. Platforma respectă GDPR și oferă audit log complet. Detalii suplimentare sunt disponibile în Pachetul de Securitate.",
  },
];

const evidenceTabs = [
  { label: "Notificare", active: true },
  { label: "Risc", active: false },
  { label: "Maturitate și remediere", active: false },
  { label: "Guvernanță", active: false },
  { label: "Detectare", active: false },
  { label: "+7", active: false },
];

const personaTabs = [
  { label: "Management", active: true },
  { label: "CISO / IT", active: false },
  { label: "Conformitate / Juridic", active: false },
  { label: "Consultant", active: false },
];

const LandingAlternativeV31 = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const startEvaluation = () => {
    posthog.capture(POSTHOG_EVENTS.START_EVALUATION);
    navigate("/identificare-raportare");
  };

  const requestDemo = () => navigate("/contact");

  return (
    <div className="la31-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .la31-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .la31-page { background: #F7FAFF; color: #0F172A; font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
        .la31-page a, .la31-page button { font-family: inherit; }
        .la31-wrap { width: min(1296px, calc(100% - 144px)); margin: 0 auto; }

        .la31-eyebrow { font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; color: #2563EB; font-weight: 600; }
        .la31-h2 { font-size: clamp(32px, 4.2vw, 42px); line-height: 1.08; letter-spacing: -0.03em; font-weight: 700; color: #0F172A; }
        .la31-btn { border: none; border-radius: 999px; padding: 12px 24px; font-weight: 500; font-size: 14px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 150ms ease; text-decoration: none; }
        .la31-btn-primary { background: #FF8400; color: #111111; }
        .la31-btn-primary:hover { background: #FF9520; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }
        .la31-btn-secondary { background: #F2F3F0; color: #111111; border: 1px solid #CBCCC9; box-shadow: 0 1px 1.75px rgba(0,0,0,0.05); }
        .la31-btn-secondary:hover { background: #E7E8E5; }
        .la31-btn-ghost-light { background: transparent; color: #93C5FD; border: 1px solid #334155; }
        .la31-btn-ghost-light:hover { border-color: #60A5FA; color: #BFDBFE; }
        .la31-tab { border: 1px solid #CBD5E1; background: #FFFFFF; padding: 8px 14px; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #334155; cursor: pointer; transition: all 150ms ease; }
        .la31-tab.active { background: #111827; border-color: #111827; color: #FFFFFF; }

        /* ===== TOP BANNER — urgency with penalty ===== */
        .la31-banner { position: sticky; top: 0; z-index: 100; height: 44px; display: flex; align-items: center; justify-content: center; gap: 16px; padding: 0 20px; border-bottom: 1px solid #1E293B; background: linear-gradient(90deg, #0B1220 0%, #111827 50%, #0B1220 100%); }
        .la31-banner-msg { font-size: 11px; color: #CBD5E1; letter-spacing: 0.5px; }
        .la31-banner-msg strong { color: #FCA5A5; font-weight: 600; }
        .la31-banner-cta { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; background: #FF8400; border: none; border-radius: 999px; font-size: 11px; font-weight: 600; color: #111111; cursor: pointer; transition: all 150ms ease; white-space: nowrap; }
        .la31-banner-cta:hover { background: #FF9520; }

        /* ===== HERO ===== */
        .la31-hero { background: linear-gradient(145deg, #0B1220 0%, #0E1A34 45%, #123A7A 100%); }
        .la31-hero-inner { padding: 24px 0 80px; }
        .la31-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
        .la31-logo { display: flex; align-items: center; gap: 10px; }
        .la31-logo-mark { width: 14px; height: 14px; background: #2563EB; border-radius: 2px; }
        .la31-logo-text { font-size: 18px; font-weight: 700; color: #FFFFFF; }
        .la31-nav-links { display: flex; align-items: center; gap: 24px; }
        .la31-nav-link { font-size: 12px; letter-spacing: 1px; color: #CBD5E1; cursor: pointer; transition: color 150ms; background: none; border: none; font-family: inherit; padding: 0; }
        .la31-nav-link:hover { color: #FFFFFF; }
        .la31-menu-btn { display: none; border: 1px solid #334155; background: transparent; color: #E2E8F0; border-radius: 10px; width: 42px; height: 42px; align-items: center; justify-content: center; cursor: pointer; }
        .la31-mobile-menu { display: none; border: 1px solid #334155; background: #0B1220; border-radius: 14px; padding: 16px; margin-bottom: 24px; }
        .la31-mobile-menu .la31-nav-links { flex-direction: column; align-items: flex-start; gap: 12px; margin-bottom: 12px; }

        .la31-hero-body { display: flex; align-items: center; gap: 48px; }
        .la31-hero-left { flex: 1; display: flex; flex-direction: column; gap: 20px; }
        .la31-hero-personas { display: flex; flex-wrap: wrap; gap: 8px; }
        .la31-hero-persona-pill { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; background: rgba(37,99,235,0.15); border: 1px solid rgba(37,99,235,0.3); border-radius: 999px; font-size: 11px; font-weight: 500; color: #93C5FD; letter-spacing: 0.5px; }
        .la31-hero-persona-pill svg { width: 14px; height: 14px; }
        .la31-hero-title { font-size: clamp(40px, 5.8vw, 58px); line-height: 1.03; letter-spacing: -0.038em; font-weight: 800; color: #FFFFFF; max-width: 640px; }
        .la31-hero-sub { font-size: 16px; line-height: 1.45; color: #C7D2FE; max-width: 580px; }
        .la31-hero-ctas { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
        .la31-hero-proof { font-size: 12px; letter-spacing: 0.5px; color: #94A3B8; display: flex; align-items: center; gap: 8px; }
        .la31-hero-proof strong { color: #E2E8F0; font-weight: 600; }
        .la31-hero-visual { width: 520px; flex-shrink: 0; background: #0B1220; border: 1px solid rgba(96,165,250,0.4); padding: 14px; display: flex; flex-direction: column; gap: 12px; }
        .la31-hero-visual-head { display: flex; justify-content: space-between; align-items: center; }
        .la31-hero-visual-head span { font-size: 10px; letter-spacing: 0.9px; }
        .la31-hero-visual-head .left { color: #DBEAFE; }
        .la31-hero-visual-head .right { color: #93C5FD; }
        .la31-hero-slide { background: #0F172A; border: 1px solid #1E3A8A; padding: 14px; display: flex; flex-direction: column; gap: 8px; }
        .la31-hero-slide-tag { font-size: 10px; letter-spacing: 1.1px; color: #60A5FA; }
        .la31-hero-slide-title { font-size: 18px; font-weight: 700; line-height: 1.2; color: #F8FAFC; }
        .la31-hero-slide-desc { font-size: 11px; line-height: 1.4; color: #BFDBFE; }
        .la31-hero-slide-meta { font-size: 10px; letter-spacing: 0.6px; color: #93C5FD; }
        .la31-hero-controls { display: flex; justify-content: space-between; align-items: center; }
        .la31-hero-controls span { font-size: 10px; letter-spacing: 0.8px; color: #93C5FD; cursor: pointer; }
        .la31-hero-dots { display: flex; gap: 6px; align-items: center; }
        .la31-hero-dot { width: 6px; height: 6px; border-radius: 50%; background: #334155; }
        .la31-hero-dot.active { background: #60A5FA; }

        /* ===== SOCIAL PROOF BAR ===== */
        .la31-proof-bar { background: #FFFFFF; border-bottom: 1px solid #E2E8F0; padding: 28px 0; }
        .la31-proof-bar-inner { display: flex; align-items: center; justify-content: center; gap: 48px; flex-wrap: wrap; }
        .la31-proof-stat { text-align: center; }
        .la31-proof-stat-val { font-size: 36px; font-weight: 800; letter-spacing: -1px; color: #0F172A; }
        .la31-proof-stat-label { font-size: 11px; letter-spacing: 0.8px; color: #64748B; margin-top: 2px; }
        .la31-proof-divider { width: 1px; height: 48px; background: #E2E8F0; }
        .la31-proof-frameworks { display: flex; align-items: center; gap: 10px; }
        .la31-proof-chip { background: #F8FAFC; border: 1px solid #E2E8F0; padding: 8px 14px; font-size: 11px; font-weight: 600; letter-spacing: 1px; color: #334155; }

        /* ===== TRUST SECTION ===== */
        .la31-trust { background: #FFFFFF; padding: 56px 0; }
        .la31-trust-title { font-size: 38px; font-weight: 700; letter-spacing: -1.2px; line-height: 1.1; color: #0F172A; margin-top: 16px; max-width: 800px; }
        .la31-trust-body { display: flex; gap: 24px; margin-top: 28px; }
        .la31-trust-main { flex: 1; display: flex; flex-direction: column; gap: 20px; }
        .la31-trust-quote { width: 420px; flex-shrink: 0; background: #0F172A; border: 1px solid #2E4A86; padding: 24px; display: flex; flex-direction: column; gap: 16px; position: relative; }
        .la31-trust-quote::before { content: '"'; position: absolute; top: 12px; left: 20px; font-size: 48px; color: #2563EB; opacity: 0.4; font-family: Georgia, serif; line-height: 1; }
        .la31-trust-quote-text { font-size: 18px; line-height: 1.3; color: #F8FAFC; padding-top: 8px; }
        .la31-trust-quote-author { display: flex; flex-direction: column; gap: 2px; }
        .la31-trust-quote-name { font-size: 13px; font-weight: 600; color: #E2E8F0; }
        .la31-trust-quote-role { font-size: 11px; letter-spacing: 0.5px; color: #94A3B8; }
        .la31-trust-benefits { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .la31-trust-benefit { display: flex; align-items: flex-start; gap: 10px; padding: 14px; background: #F8FAFC; border: 1px solid #E2E8F0; }
        .la31-trust-benefit-icon { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; background: #EAF2FF; border-radius: 6px; flex-shrink: 0; color: #2563EB; }
        .la31-trust-benefit-text { font-size: 13px; line-height: 1.4; color: #334155; }
        .la31-trust-benefit-text strong { color: #0F172A; display: block; font-size: 14px; margin-bottom: 2px; }

        /* ===== OPERATING FLOW ===== */
        .la31-flow { background: #F8FAFC; padding: 72px 0; }
        .la31-flow-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 28px; }
        .la31-flow-card { background: #FFFFFF; border: 1px solid #E2E8F0; padding: 24px 20px; display: flex; flex-direction: column; gap: 12px; position: relative; }
        .la31-flow-card-num { font-size: 11px; letter-spacing: 1.5px; color: #1D4ED8; font-weight: 600; }
        .la31-flow-card-title { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; color: #0F172A; }
        .la31-flow-card-desc { font-size: 13px; line-height: 1.45; color: #475569; flex: 1; }
        .la31-flow-card-metric { display: flex; align-items: center; gap: 6px; padding: 8px 12px; background: #EAF2FF; border-radius: 6px; font-size: 12px; font-weight: 600; color: #1D4ED8; margin-top: 4px; }
        .la31-flow-card-metric svg { width: 14px; height: 14px; }
        .la31-flow-cta { margin-top: 22px; display: flex; gap: 12px; align-items: center; }

        /* ===== LEGAL FRAMEWORK ===== */
        .la31-legal { background: #F8FBFF; padding: 80px 0; }
        .la31-legal-title { font-size: 46px; font-weight: 800; letter-spacing: -1.5px; line-height: 1.06; color: #0F172A; margin-top: 16px; }
        .la31-legal-sub { font-size: 14px; line-height: 1.5; color: #475569; margin-top: 12px; max-width: 700px; }
        .la31-legal-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 24px; }
        .la31-legal-card { background: #FFFFFF; border: 1px solid #D6E6FF; padding: 24px 22px; display: flex; flex-direction: column; gap: 10px; }
        .la31-legal-card-tag { font-size: 11px; letter-spacing: 1.2px; font-weight: 600; }
        .la31-legal-card-title { font-size: 24px; font-weight: 700; letter-spacing: -0.6px; color: #0F172A; }
        .la31-legal-card-sub { font-size: 12px; line-height: 1.4; }
        .la31-legal-card-desc { font-size: 12px; line-height: 1.5; color: #475569; }
        .la31-legal-warn { background: #FEF2F2; border: 1px solid #FECACA; padding: 20px 22px; display: flex; gap: 14px; align-items: flex-start; margin-top: 18px; }
        .la31-legal-warn-icon { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; background: #FEE2E2; border-radius: 8px; flex-shrink: 0; color: #DC2626; }
        .la31-legal-warn-content { flex: 1; }
        .la31-legal-warn-title { font-size: 18px; font-weight: 700; letter-spacing: -0.3px; color: #991B1B; }
        .la31-legal-warn-desc { font-size: 13px; line-height: 1.55; color: #7F1D1D; margin-top: 4px; }
        .la31-legal-warn-desc strong { font-weight: 700; }

        /* ===== SEPARATOR CTA ===== */
        .la31-separator { background: #020617; padding: 56px 0; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; position: relative; overflow: hidden; }
        .la31-separator::before { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, #020617 0%, rgba(0,0,0,0.56) 50%, #020617 100%); z-index: 1; }
        .la31-separator > * { position: relative; z-index: 2; }
        .la31-separator-title { font-size: clamp(36px, 5vw, 52px); font-weight: 800; letter-spacing: -1.6px; line-height: 1.06; color: #FFFFFF; max-width: 980px; }
        .la31-separator-sub { font-size: 15px; line-height: 1.45; color: #E2E8F0; max-width: 760px; margin-top: 14px; }
        .la31-separator-cta { margin-top: 20px; }

        /* ===== PRODUCT EVIDENCE ===== */
        .la31-evidence { background: #F8FAFC; padding: 80px 0; }
        .la31-evidence-tabs { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
        .la31-evidence-body { display: flex; gap: 20px; margin-top: 20px; }
        .la31-evidence-main { flex: 1; display: flex; flex-direction: column; gap: 10px; }
        .la31-evidence-main-title { font-size: 24px; font-weight: 700; letter-spacing: -0.6px; }
        .la31-evidence-shot { border: 1px solid #334155; background: #0F172A; height: 320px; display: flex; align-items: center; justify-content: center; color: #93C5FD; font-size: 13px; letter-spacing: 0.5px; }
        .la31-evidence-side { width: 400px; flex-shrink: 0; display: flex; flex-direction: column; gap: 12px; }
        .la31-evidence-video-title { font-size: 24px; font-weight: 700; letter-spacing: -0.6px; }
        .la31-evidence-video-card { background: #FFFFFF; border: 1px solid #CBD5E1; display: flex; flex-direction: column; gap: 10px; }
        .la31-evidence-video-shot { border: 1px solid #334155; background: #111827; height: 190px; display: flex; align-items: center; justify-content: center; color: #94A3B8; font-size: 13px; }
        .la31-evidence-video-note { font-size: 12px; line-height: 1.45; color: #475569; padding: 0 14px 14px; }

        /* ===== CAPABILITIES ===== */
        .la31-capabilities { background: #FFFFFF; padding: 80px 0; }
        .la31-cap-tabs { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
        .la31-cap-body { display: flex; gap: 20px; margin-top: 20px; }
        .la31-cap-list { flex: 1; display: flex; flex-direction: column; gap: 12px; }
        .la31-cap-card { background: #F8FAFC; border: 1px solid #E2E8F0; padding: 16px; display: flex; flex-direction: column; gap: 8px; }
        .la31-cap-card-title { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; color: #0F172A; }
        .la31-cap-card-desc { font-size: 13px; line-height: 1.45; color: #475569; }
        .la31-persona-card { width: 420px; flex-shrink: 0; background: #0F172A; border: 1px solid #1E293B; padding: 24px; display: flex; flex-direction: column; gap: 14px; }
        .la31-persona-title { font-size: 28px; font-weight: 700; letter-spacing: -0.8px; color: #FFFFFF; }
        .la31-persona-bullet { font-size: 13px; line-height: 1.45; color: #C7D2FE; }

        /* ===== WHY NOT CONSULTING ===== */
        .la31-compare { background: #F8FAFC; padding: 80px 0; border-top: 1px solid #E2E8F0; }
        .la31-compare-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; margin-top: 28px; border: 1px solid #E2E8F0; overflow: hidden; }
        .la31-compare-col { display: flex; flex-direction: column; }
        .la31-compare-header { padding: 16px 20px; font-size: 14px; font-weight: 700; letter-spacing: -0.3px; }
        .la31-compare-header.traditional { background: #F1F5F9; color: #64748B; border-bottom: 1px solid #E2E8F0; }
        .la31-compare-header.platform { background: #0F172A; color: #FFFFFF; }
        .la31-compare-row { padding: 12px 20px; font-size: 13px; line-height: 1.4; border-bottom: 1px solid #F1F5F9; display: flex; align-items: center; gap: 8px; min-height: 48px; }
        .la31-compare-row.traditional { color: #64748B; background: #FFFFFF; }
        .la31-compare-row.platform { color: #C7D2FE; background: #0F172A; }
        .la31-compare-row .check { color: #10B981; flex-shrink: 0; }
        .la31-compare-row .cross { color: #EF4444; flex-shrink: 0; }
        .la31-compare-cta { margin-top: 28px; text-align: center; }

        /* ===== FAQ ===== */
        .la31-faq { background: #FFFFFF; padding: 80px 0; }
        .la31-faq-list { margin-top: 24px; max-width: 900px; }
        .la31-faq-item { border-bottom: 1px solid #CBCCC9; }
        .la31-faq-trigger { display: flex; justify-content: space-between; align-items: center; gap: 18px; padding: 16px 0; cursor: pointer; width: 100%; background: none; border: none; text-align: left; font-family: inherit; }
        .la31-faq-trigger-text { font-size: 16px; font-weight: 500; line-height: 1.5; color: #111111; }
        .la31-faq-answer { padding: 0 0 16px; font-size: 14px; line-height: 1.5; color: #666666; max-width: 800px; }

        /* ===== FINAL CTA ===== */
        .la31-final-cta { background: linear-gradient(145deg, #0B2452 0%, #0F2E6D 50%, #1E40AF 100%); padding: 80px 0; }
        .la31-final-label { color: #93C5FD; }
        .la31-final-title { font-size: clamp(32px, 4.2vw, 46px); font-weight: 800; letter-spacing: -1.8px; line-height: 1.08; color: #FFFFFF; max-width: 800px; }
        .la31-final-desc { font-size: 14px; line-height: 1.5; color: #DBEAFE; margin-top: 12px; max-width: 600px; }
        .la31-final-btns { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
        .la31-final-checklist { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 20px; }
        .la31-final-check { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #93C5FD; letter-spacing: 0.3px; }
        .la31-final-check svg { color: #34D399; width: 14px; height: 14px; }

        /* ===== FOOTER ===== */
        .la31-footer { background: #0B2452; padding: 48px 0; }
        .la31-footer-top { display: flex; justify-content: space-between; align-items: start; gap: 24px; flex-wrap: wrap; }
        .la31-footer-brand { font-size: 24px; font-weight: 700; color: #FFFFFF; }
        .la31-footer-tagline { font-size: 12px; letter-spacing: 1px; color: #94A3B8; margin-top: 6px; }
        .la31-footer-nav { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
        .la31-footer-link { font-size: 12px; letter-spacing: 1px; color: #E2E8F0; cursor: pointer; background: none; border: none; font-family: inherit; transition: color 150ms; }
        .la31-footer-link:hover { color: #FFFFFF; }
        .la31-footer-line { height: 1px; background: #1E293B; margin: 24px 0 14px; border: none; }
        .la31-footer-copy { font-size: 11px; letter-spacing: 0.8px; color: #64748B; }

        /* ===== MOBILE CTA ===== */
        .la31-mobile-cta { display: none; position: sticky; bottom: 0; z-index: 80; background: rgba(11,18,32,0.94); backdrop-filter: blur(10px); border-top: 1px solid #334155; padding: 10px 12px; }
        .la31-mobile-cta button { width: 100%; justify-content: center; }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 1080px) {
          .la31-hero-body, .la31-trust-body, .la31-cap-body, .la31-evidence-body { flex-direction: column; }
          .la31-hero-visual, .la31-trust-quote, .la31-persona-card, .la31-evidence-side { width: 100%; }
          .la31-legal-cards, .la31-flow-cards { grid-template-columns: 1fr; }
          .la31-compare-grid { grid-template-columns: 1fr; }
          .la31-compare-col.traditional { order: 2; }
          .la31-trust-benefits { grid-template-columns: 1fr; }
        }
        @media (max-width: 920px) {
          .la31-wrap { width: min(1296px, calc(100% - 32px)); }
          .la31-nav-links, .la31-nav .la31-btn-secondary { display: none; }
          .la31-menu-btn { display: inline-flex; }
          .la31-mobile-menu { display: block; }
          .la31-mobile-cta { display: flex; }
          .la31-banner-msg { font-size: 10px; }
          .la31-proof-bar-inner { gap: 24px; }
          .la31-proof-divider { display: none; }
        }
      `}</style>

      {/* ====== TOP BANNER — penalty urgency ====== */}
      <div className="la31-banner">
        <span className="la31-banner-msg">
          Termen NIS2: 30 de zile de la notificare · Amenzi până la{" "}
          <strong>10M EUR sau 2% din cifra de afaceri</strong>
        </span>
        <button
          type="button"
          className="la31-banner-cta"
          onClick={startEvaluation}
        >
          Verifică obligațiile <ArrowRight size={12} />
        </button>
      </div>

      {/* ====== HERO ====== */}
      <section className="la31-hero">
        <div className="la31-wrap la31-hero-inner">
          <nav className="la31-nav">
            <div className="la31-logo">
              <span className="la31-logo-mark" />
              <span className="la31-logo-text">SECURENIS</span>
            </div>
            <div className="la31-nav-links">
              <span className="la31-nav-link">Platformă</span>
              <span className="la31-nav-link">Soluții</span>
              <button
                type="button"
                className="la31-nav-link"
                onClick={() => navigate("/preturi")}
              >
                Prețuri
              </button>
              <span className="la31-nav-link">Resurse</span>
            </div>
            <button
              type="button"
              className="la31-btn la31-btn-secondary"
              onClick={requestDemo}
            >
              Solicita demo
            </button>
            <button
              type="button"
              className="la31-menu-btn"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </nav>

          {mobileMenuOpen && (
            <div className="la31-mobile-menu">
              <div className="la31-nav-links">
                <span className="la31-nav-link">Platformă</span>
                <span className="la31-nav-link">Soluții</span>
                <button
                  type="button"
                  className="la31-nav-link"
                  onClick={() => navigate("/preturi")}
                >
                  Prețuri
                </button>
                <span className="la31-nav-link">Resurse</span>
              </div>
              <button
                type="button"
                className="la31-btn la31-btn-secondary"
                onClick={requestDemo}
              >
                Solicita demo
              </button>
            </div>
          )}

          <div className="la31-hero-body">
            <div className="la31-hero-left">
              {/* Persona pills — visitor immediately sees "this is for me" */}
              <div className="la31-hero-personas">
                <span className="la31-hero-persona-pill">
                  <Users size={14} /> Management
                </span>
                <span className="la31-hero-persona-pill">
                  <Shield size={14} /> CISO
                </span>
                <span className="la31-hero-persona-pill">
                  <Scale size={14} /> Juridic
                </span>
                <span className="la31-hero-persona-pill">
                  <FileCheck size={14} /> Consultant
                </span>
              </div>

              <h1 className="la31-hero-title">
                Conformitate NIS2, cu impact real în primele 30 de zile.
              </h1>
              <p className="la31-hero-sub">
                Automatizezi notificarea, maparea cerințelor și evidența pentru
                DNSC într-un flux unificat. De la CUI la dosar audit-ready.
              </p>
              <div className="la31-hero-ctas">
                <button
                  type="button"
                  className="la31-btn la31-btn-primary"
                  onClick={startEvaluation}
                >
                  Evaluare gratuită — 5 min <ArrowRight size={16} />
                </button>
                <button
                  type="button"
                  className="la31-btn la31-btn-ghost-light"
                  onClick={requestDemo}
                >
                  Solicita demo enterprise
                </button>
              </div>
              <p className="la31-hero-proof">
                <Timer size={14} /> Timp mediu prima evaluare:{" "}
                <strong>39 minute</strong> · Actualizat martie 2026
              </p>
            </div>

            <div className="la31-hero-visual">
              <div className="la31-hero-visual-head">
                <span className="left">Noutăți DNSC și calendar NIS2</span>
                <span className="right">Ecran 1/3</span>
              </div>
              <div className="la31-hero-slide">
                <span className="la31-hero-slide-tag">
                  ACTUALIZARE REGLEMENTARE
                </span>
                <p className="la31-hero-slide-title">
                  Ordinele DNSC 1/2025 și 2/2025 operaționalizează notificarea
                  și evaluarea riscului.
                </p>
                <p className="la31-hero-slide-desc">
                  Context: Directiva UE 2022/2555 → termen transpunere 17 oct
                  2024 → cadrul RO prin OUG 155/2024 și Legea 124/2025.
                </p>
                <span className="la31-hero-slide-meta">
                  Sursa: flux public DNSC / legislatie NIS2
                </span>
              </div>
              <div className="la31-hero-controls">
                <span>&lt; Prev</span>
                <div className="la31-hero-dots">
                  <span className="la31-hero-dot active" />
                  <span className="la31-hero-dot" />
                  <span className="la31-hero-dot" />
                </div>
                <span>Next &gt;</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SOCIAL PROOF BAR — immediate credibility ====== */}
      <section className="la31-proof-bar">
        <div className="la31-proof-bar-inner">
          <div className="la31-proof-stat">
            <p className="la31-proof-stat-val">420+</p>
            <p className="la31-proof-stat-label">evaluări generate</p>
          </div>
          <span className="la31-proof-divider" />
          <div className="la31-proof-stat">
            <p className="la31-proof-stat-val">217+</p>
            <p className="la31-proof-stat-label">controale CyFUN integrate</p>
          </div>
          <span className="la31-proof-divider" />
          <div className="la31-proof-stat">
            <p className="la31-proof-stat-val">4x</p>
            <p className="la31-proof-stat-label">
              mai rapid vs. consultanță manuală
            </p>
          </div>
          <span className="la31-proof-divider" />
          <div className="la31-proof-frameworks">
            <span className="la31-proof-chip">Anexa 1</span>
            <span className="la31-proof-chip">Anexa 2</span>
            <span className="la31-proof-chip">CyFUN</span>
            <span className="la31-proof-chip">NIST-CSF</span>
          </div>
        </div>
      </section>

      {/* ====== TRUST — quote with named author ====== */}
      <section className="la31-trust">
        <div className="la31-wrap">
          <p className="la31-eyebrow">ÎNCREDERE ANALIȘTI ȘI CLIENȚI</p>
          <h2 className="la31-trust-title">
            O platformă pentru echipele de IT, legal, risc și conformitate.
          </h2>
          <div className="la31-trust-body">
            <div className="la31-trust-main">
              <div className="la31-trust-benefits">
                <div className="la31-trust-benefit">
                  <div className="la31-trust-benefit-icon">
                    <Zap size={16} />
                  </div>
                  <div className="la31-trust-benefit-text">
                    <strong>Automatizare completă</strong>
                    De la identificare la dosar audit, fără documente manuale.
                  </div>
                </div>
                <div className="la31-trust-benefit">
                  <div className="la31-trust-benefit-icon">
                    <Shield size={16} />
                  </div>
                  <div className="la31-trust-benefit-text">
                    <strong>Aliniere CyFUN nativă</strong>
                    217+ controale integrate din cadrul DNSC.
                  </div>
                </div>
                <div className="la31-trust-benefit">
                  <div className="la31-trust-benefit-icon">
                    <Users size={16} />
                  </div>
                  <div className="la31-trust-benefit-text">
                    <strong>Flux multi-rol</strong>
                    Un singur loc pentru management, CISO și juridic.
                  </div>
                </div>
                <div className="la31-trust-benefit">
                  <div className="la31-trust-benefit-icon">
                    <FileCheck size={16} />
                  </div>
                  <div className="la31-trust-benefit-text">
                    <strong>Trasabilitate completă</strong>
                    Fiecare decizie cu justificare, timestamp și versiune.
                  </div>
                </div>
              </div>
            </div>
            <aside className="la31-trust-quote">
              <p className="la31-trust-quote-text">
                O singură platformă pentru execuția riscului, conformității,
                securității cibernetice și auditului. Echipa poate explica
                fiecare decizie în 2 minute.
              </p>
              <div className="la31-trust-quote-author">
                <span className="la31-trust-quote-name">Maria Ionescu</span>
                <span className="la31-trust-quote-role">
                  Șef Guvernanță · Operator servicii esențiale
                </span>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ====== OPERATING FLOW — with time metrics ====== */}
      <section className="la31-flow">
        <div className="la31-wrap">
          <p className="la31-eyebrow">FLUX OPERAȚIONAL</p>
          <h2 className="la31-h2">Trei pași de la CUI la dosar audit-ready.</h2>
          <div className="la31-flow-cards">
            <article className="la31-flow-card">
              <span className="la31-flow-card-num">PASUL 01</span>
              <h3 className="la31-flow-card-title">Obligativitate entitati</h3>
              <p className="la31-flow-card-desc">
                Pornești cu CUI. Platforma identifică automat sectorul,
                criticitatea și ipoteza de încadrare conform OUG 155/2024.
              </p>
              <div className="la31-flow-card-metric">
                <Clock size={14} /> ~5 minute pentru clasificare
              </div>
            </article>
            <article className="la31-flow-card">
              <span className="la31-flow-card-num">PASUL 02</span>
              <h3 className="la31-flow-card-title">
                Implementare NIS2 (CyFUN)
              </h3>
              <p className="la31-flow-card-desc">
                Rulezi analize de risc, evaluare maturitate și planuri de
                remediere cu evidență justificativă pe 217+ controale.
              </p>
              <div className="la31-flow-card-metric">
                <Clock size={14} /> ~30 minute pentru evaluare completă
              </div>
            </article>
            <article className="la31-flow-card">
              <span className="la31-flow-card-num">PASUL 03</span>
              <h3 className="la31-flow-card-title">
                Auditare și îmbunătățire continuă
              </h3>
              <p className="la31-flow-card-desc">
                Generezi dosar cu decizii, versiuni și recomandări. Monitorizezi
                progresul și raportezi către DNSC.
              </p>
              <div className="la31-flow-card-metric">
                <FileCheck size={14} /> Export dosar audit-ready instant
              </div>
            </article>
          </div>
          <div className="la31-flow-cta">
            <button
              type="button"
              className="la31-btn la31-btn-primary"
              onClick={startEvaluation}
            >
              Testează fluxul gratuit <ArrowRight size={16} />
            </button>
            <button
              type="button"
              className="la31-btn la31-btn-secondary"
              onClick={requestDemo}
            >
              Vezi demo ghidat
            </button>
          </div>
        </div>
      </section>

      {/* ====== LEGAL FRAMEWORK — moved up for regulatory credibility ====== */}
      <section className="la31-legal">
        <div className="la31-wrap">
          <p className="la31-eyebrow">CADRU REGLEMENTAR</p>
          <h2 className="la31-legal-title">Baza legală NIS2 în România</h2>
          <p className="la31-legal-sub">
            Construit pe Directiva (UE) 2022/2555, OUG 155/2024 și Legea
            124/2025 pentru programe de guvernanță enterprise.
          </p>
          <div className="la31-legal-cards">
            <article className="la31-legal-card">
              <span
                className="la31-legal-card-tag"
                style={{ color: "#60A5FA" }}
              >
                DIRECTIVA UE
              </span>
              <h3 className="la31-legal-card-title">
                Directiva (UE) 2022/2555
              </h3>
              <span
                className="la31-legal-card-sub"
                style={{ color: "#60A5FA" }}
              >
                NIS2 - cadrul european pentru securitate cibernetică
              </span>
              <p className="la31-legal-card-desc">
                Standarde minime de securitate pentru organizatiile din sectoare
                critice.
              </p>
            </article>
            <article className="la31-legal-card">
              <span
                className="la31-legal-card-tag"
                style={{ color: "#F59E0B" }}
              >
                TRANSPUNERE
              </span>
              <h3 className="la31-legal-card-title">OUG nr. 155/2024</h3>
              <span
                className="la31-legal-card-sub"
                style={{ color: "#F59E0B" }}
              >
                Transpunere națională
              </span>
              <p className="la31-legal-card-desc">
                Stabileste cadrul legal national pentru identificare, evaluare
                risc si obligatii de raportare.
              </p>
            </article>
            <article className="la31-legal-card">
              <span
                className="la31-legal-card-tag"
                style={{ color: "#10B981" }}
              >
                LEGE
              </span>
              <h3 className="la31-legal-card-title">Legea nr. 124/2025</h3>
              <span
                className="la31-legal-card-sub"
                style={{ color: "#10B981" }}
              >
                Aprobare si completare
              </span>
              <p className="la31-legal-card-desc">
                Finalizeaza transpunerea NIS2 in legislatia nationala si
                clarifica cerintele operationale.
              </p>
            </article>
          </div>
          {/* Penalty warning — red instead of blue for urgency */}
          <div className="la31-legal-warn">
            <div className="la31-legal-warn-icon">
              <ShieldAlert size={20} />
            </div>
            <div className="la31-legal-warn-content">
              <h3 className="la31-legal-warn-title">
                Amenzi până la 10.000.000 EUR sau 2% din cifra de afaceri
              </h3>
              <p className="la31-legal-warn-desc">
                Neconformarea atrage sancțiuni financiare,{" "}
                <strong>răspundere personală a conducerii</strong> și posibila
                interdicție temporară a activității. Termenele: notificare în 30
                de zile, autoevaluare risc în 60 de zile, monitorizare continuă.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SEPARATOR CTA ====== */}
      <section className="la31-separator">
        <h2 className="la31-separator-title">
          SECURENIS automatizează conformitatea, economisind sute de ore.
        </h2>
        <p className="la31-separator-sub">
          Fii cu un pas înaintea cerințelor DNSC și evită riscul de amenzi.
        </p>
        <div className="la31-separator-cta">
          <button
            type="button"
            className="la31-btn la31-btn-primary"
            onClick={startEvaluation}
          >
            Începe evaluarea gratuită <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* ====== PRODUCT EVIDENCE ====== */}
      <section className="la31-evidence">
        <div className="la31-wrap">
          <p className="la31-eyebrow">DOVEZI PRODUS</p>
          <h2 className="la31-h2">
            Dovezi vizuale: clasificare, risc, maturitate.
          </h2>
          <div className="la31-evidence-tabs">
            {evidenceTabs.map((tab) => (
              <span
                key={tab.label}
                className={`la31-tab${tab.active ? " active" : ""}`}
              >
                {tab.label}
              </span>
            ))}
          </div>
          <div className="la31-evidence-body">
            <div className="la31-evidence-main">
              <h3 className="la31-evidence-main-title">
                Rezultat clasificare + context regulator
              </h3>
              <div className="la31-evidence-shot">
                Screenshot placeholder 16:9
              </div>
            </div>
            <aside className="la31-evidence-side">
              <h3 className="la31-evidence-video-title">
                Demonstrație 60-90 sec
              </h3>
              <div className="la31-evidence-video-card">
                <div className="la31-evidence-video-shot">
                  Video placeholder
                </div>
                <p className="la31-evidence-video-note">
                  Include scenariu complet: input CUI → analiză risc → evidență
                  exportabilă.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ====== CAPABILITIES ====== */}
      <section className="la31-capabilities">
        <div className="la31-wrap">
          <p className="la31-eyebrow">CAPABILITATI PLATFORMA</p>
          <h2 className="la31-h2">Claritate pentru toate rolurile implicate</h2>
          <div className="la31-cap-tabs">
            {personaTabs.map((tab) => (
              <span
                key={tab.label}
                className={`la31-tab${tab.active ? " active" : ""}`}
              >
                {tab.label}
              </span>
            ))}
          </div>
          <div className="la31-cap-body">
            <div className="la31-cap-list">
              <article className="la31-cap-card">
                <h3 className="la31-cap-card-title">
                  Clasificare sectoriala, planificare, rezultate
                </h3>
                <p className="la31-cap-card-desc">
                  Reguli explicite de incadrare cu justificari salvate la nivel
                  de decizie.
                </p>
              </article>
              <article className="la31-cap-card">
                <h3 className="la31-cap-card-title">
                  Risc, maturitate, remediere
                </h3>
                <p className="la31-cap-card-desc">
                  Prioritizare orientata business cu planuri actionabile si
                  responsabilitati clare.
                </p>
              </article>
              <article className="la31-cap-card">
                <h3 className="la31-cap-card-title">Auditare, raportare</h3>
                <p className="la31-cap-card-desc">
                  Versionare evidenta, log de decizie si livrabile pentru audit
                  intern si extern.
                </p>
              </article>
            </div>
            <aside className="la31-persona-card">
              <h3 className="la31-persona-title">Pentru management</h3>
              <p className="la31-persona-bullet">
                • Vizibilitate asupra riscurilor critice pe entități
              </p>
              <p className="la31-persona-bullet">
                • Justificări ușor de prezentat în board și audit
              </p>
              <p className="la31-persona-bullet">
                • Roadmap trimestrial cu progres măsurabil
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* ====== WHY NOT CONSULTING — competitive differentiation ====== */}
      <section className="la31-compare">
        <div className="la31-wrap">
          <p className="la31-eyebrow">DE CE SECURENIS</p>
          <h2 className="la31-h2">Platforma vs. consultanță tradițională</h2>
          <div className="la31-compare-grid">
            <div className="la31-compare-col">
              <div className="la31-compare-header traditional">
                Consultanță manuală
              </div>
              <div className="la31-compare-row traditional">
                <span className="cross">✕</span> 6-12 luni timp de implementare
              </div>
              <div className="la31-compare-row traditional">
                <span className="cross">✕</span> Documente statice, fără
                versionare
              </div>
              <div className="la31-compare-row traditional">
                <span className="cross">✕</span> Dependență de disponibilitatea
                consultantului
              </div>
              <div className="la31-compare-row traditional">
                <span className="cross">✕</span> Cost variabil, greu de bugetat
              </div>
              <div className="la31-compare-row traditional">
                <span className="cross">✕</span> Fără trasabilitate automată a
                deciziilor
              </div>
            </div>
            <div className="la31-compare-col">
              <div className="la31-compare-header platform">
                SECURENIS Platform
              </div>
              <div className="la31-compare-row platform">
                <span className="check">✓</span> Evaluare inițială în 39 de
                minute
              </div>
              <div className="la31-compare-row platform">
                <span className="check">✓</span> Dosar versionat cu istoric
                complet
              </div>
              <div className="la31-compare-row platform">
                <span className="check">✓</span> Disponibil 24/7, progres în
                ritmul tău
              </div>
              <div className="la31-compare-row platform">
                <span className="check">✓</span> Cost predictibil, ROI măsurabil
              </div>
              <div className="la31-compare-row platform">
                <span className="check">✓</span> Audit trail automat pe fiecare
                decizie
              </div>
            </div>
          </div>
          <div className="la31-compare-cta">
            <button
              type="button"
              className="la31-btn la31-btn-primary"
              onClick={startEvaluation}
            >
              Încearcă gratuit — fără card <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* ====== FAQ — expanded with product objections ====== */}
      <section className="la31-faq">
        <div className="la31-wrap">
          <p className="la31-eyebrow">ÎNTREBĂRI FRECVENTE</p>
          <h2 className="la31-h2">
            Răspunsuri la întrebările cheie despre NIS2
          </h2>
          <div className="la31-faq-list">
            {faqItems.map((faq, i) => (
              <div key={faq.q} className="la31-faq-item">
                <button
                  type="button"
                  className="la31-faq-trigger"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  <span className="la31-faq-trigger-text">{faq.q}</span>
                  {openFaq === i ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>
                {openFaq === i && <p className="la31-faq-answer">{faq.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== FINAL CTA — with risk hook + checklist ====== */}
      <section className="la31-final-cta">
        <div className="la31-wrap">
          <p className="la31-eyebrow la31-final-label">ÎNCEPE ACUM</p>
          <h2 className="la31-final-title">
            Află în 5 minute dacă ești vizat de NIS2 și ce trebuie să faci.
          </h2>
          <p className="la31-final-desc">
            Evaluarea inițială este gratuită. Obții un raport cu obligațiile
            tale, termenele limită și pașii următori concreți.
          </p>
          <div className="la31-final-btns">
            <button
              type="button"
              className="la31-btn la31-btn-primary"
              onClick={startEvaluation}
            >
              Evaluare gratuită <ArrowRight size={16} />
            </button>
            <button
              type="button"
              className="la31-btn la31-btn-ghost-light"
              onClick={requestDemo}
            >
              Vorbește cu un consultant
            </button>
          </div>
          <div className="la31-final-checklist">
            <span className="la31-final-check">
              <Zap size={14} /> Fără card necesar
            </span>
            <span className="la31-final-check">
              <Zap size={14} /> Rezultat în 5 minute
            </span>
            <span className="la31-final-check">
              <Zap size={14} /> Date securizate UE
            </span>
            <span className="la31-final-check">
              <Zap size={14} /> Export PDF instant
            </span>
          </div>
        </div>
      </section>

      {/* ====== FOOTER ====== */}
      <footer className="la31-footer">
        <div className="la31-wrap">
          <div className="la31-footer-top">
            <div>
              <h3 className="la31-footer-brand">SECURENIS pentru companii</h3>
              <p className="la31-footer-tagline">
                Claritate de guvernanță pentru decizii NIS2.
              </p>
            </div>
            <nav className="la31-footer-nav">
              <button
                type="button"
                className="la31-footer-link"
                onClick={() => navigate("/preturi")}
              >
                Prețuri
              </button>
              <span className="la31-footer-link">Centru de Încredere</span>
              <span className="la31-footer-link">Pachet de Securitate</span>
              <button
                type="button"
                className="la31-footer-link"
                onClick={requestDemo}
              >
                Contact pentru companii
              </button>
            </nav>
          </div>
          <hr className="la31-footer-line" />
          <p className="la31-footer-copy">
            © 2026 SECURENIS · Toate drepturile rezervate
          </p>
        </div>
      </footer>

      {/* ====== MOBILE STICKY CTA — single action ====== */}
      <div className="la31-mobile-cta">
        <button
          type="button"
          className="la31-btn la31-btn-primary"
          onClick={startEvaluation}
        >
          Evaluare gratuită — 5 min <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default LandingAlternativeV31;
